import cx from 'classnames';
import { forwardRef, useLayoutEffect, useRef, useState } from 'react';
import { useIntersection } from 'react-use';
import { animateScroll } from 'react-scroll';

import ArrowDownIcon from '../../components/icons/ArrowDownIcon';
import CloseCircleIcon from '../../components/icons/closeCircleIcon';
import styles from './ScrollableTagList.module.scss';
import { capitalize } from 'lodash';
import { CloseIconV2 } from '../../components/icons/CloseIconV2';

export const ScrollableTagList = ({
  selectable = false,
  tagList,
  scrollContainerId,
  onTagClick,
  onDelete,
  onClear,
  className,
  theme = 'light',
  size = 'default',
  itemClassName,
  containerProps,
  prevBtnClassName,
  nextBtnClassName,
  version,
  designV2,
}) => {
  const [arrowsVisible, setArrowsVisible] = useState(false);
  const containerRef = useRef(null);
  const isV2 = version === '2';

  const intersectionLeftRef = useRef(null);
  const intersectionLeft = useIntersection(intersectionLeftRef, {
    root: null,
    rootMargin: '0px',
    threshold: 1,
  });
  let intersectionLeftValueRef = useRef(null);
  intersectionLeftValueRef.current = intersectionLeft;

  let intersectionRightRef = useRef(null);
  const intersectionRight = useIntersection(intersectionRightRef, {
    root: null,
    rootMargin: '0px',
    threshold: 1,
  });
  let intersectionRightValueRef = useRef(null);
  intersectionRightValueRef.current = intersectionRight;

  useLayoutEffect(() => {
    if (!intersectionRight || !intersectionLeft) return;

    const timerId = setTimeout(() => {
      const visible =
        intersectionRightValueRef.current.intersectionRatio !== 1 ||
        intersectionLeftValueRef.current.intersectionRatio !== 1;

      setArrowsVisible(visible);
    }, 30);

    return () => {
      clearTimeout(timerId);
    };
  }, [intersectionRight?.intersectionRatio, intersectionLeft?.intersectionRatio]);

  const handleNextClick = () => {
    const visibleItems = containerRef.current.querySelectorAll('[data-visible="true"]');
    const lastVisibleItem = visibleItems[visibleItems.length - 1];
    const nextItem = lastVisibleItem.nextSibling;

    if (nextItem && nextItem.dataset.visible === 'false') {
      const container = document.getElementById(scrollContainerId);
      const width = nextItem.clientWidth;
      const scrollToPx = container.scrollLeft + width;

      animateScroll.scrollTo(scrollToPx, {
        horizontal: true,
        containerId: scrollContainerId,
        duration: 200,
      });
    } else {
      animateScroll.scrollTo(10000, {
        horizontal: true,
        containerId: scrollContainerId,
        duration: 200,
      });
    }
  };

  const handlePrevClick = () => {
    const visibleItems = containerRef.current.querySelectorAll('[data-visible="true"]');
    const lastVisibleItem = visibleItems[0];
    const prevItem = lastVisibleItem.previousSibling;

    if (prevItem && prevItem.dataset.visible === 'false') {
      const container = document.getElementById(scrollContainerId);
      const width = prevItem.clientWidth;
      const scrollToPx = container.scrollLeft - width;

      animateScroll.scrollTo(scrollToPx, {
        horizontal: true,
        containerId: scrollContainerId,
        duration: 200,
      });
    } else {
      animateScroll.scrollTo(0, {
        horizontal: true,
        containerId: scrollContainerId,
        duration: 200,
      });
    }
  };

  if (tagList.length === 0) return null;

  const themeClassName = styles[`theme${capitalize(theme)}`];
  const sizeClassName = styles[`size${capitalize(size)}`];

  return (
    <div
      className={cx(
        className,
        themeClassName,
        sizeClassName,
        arrowsVisible && styles.withArrows,
        isV2 && styles.v2
      )}>
      <div className={styles.sliderContainer} {...containerProps}>
        {arrowsVisible && (
          <button className={prevBtnClassName || styles.prevButton} onClick={handlePrevClick}>
            <ArrowDownIcon />
          </button>
        )}

        <div
          id={scrollContainerId}
          className={cx(styles.scrollableContainer, arrowsVisible && styles.withArrows)}
          ref={containerRef}>
          <div className={cx(styles.observableDiv, styles.left)} ref={intersectionLeftRef} />

          {tagList.map((tag,index) => {
            return (
              <Item
                tagData={tag}
                value={tag.value}
                label={tag.label}
                key={tag.value}
                onTagClick={onTagClick}
                onDelete={onDelete}
                itemClassName={itemClassName}
                selectable={selectable}
                isV2={isV2}
                designV2={designV2}
                index={index}
              />
            );
          })}

          <div className={cx(styles.observableDiv, styles.right)} ref={intersectionRightRef} />
        </div>
        {arrowsVisible && (
          <button className={nextBtnClassName || styles.nextButton} onClick={handleNextClick}>
            <ArrowDownIcon />
          </button>
        )}

        {onClear && (
          <button type="button" className={styles.clearButton} onClick={onClear}>
            Clear all
          </button>
        )}
      </div>
    </div>
  );
};

export const Item = ({ value, label, onDelete, onTagClick, isV2, designV2, ...rest }) => {
  const intersectionRef = useRef(null);
  const intersection = useIntersection(intersectionRef, {
    root: null,
    rootMargin: '0px',
    threshold: 1,
  });

  return (
    <TagUI
      ref={intersectionRef}
      onDelete={onDelete}
      value={value}
      label={label}
      onClick={onTagClick}
      data-visible={intersection?.intersectionRatio === 1 ? 'true' : 'false'}
      isV2={isV2}
      designV2={designV2}
      key={rest?.index || rest?.key}
      {...rest}
    />
  );
};

export const TagUI = forwardRef(
  (
    { label, onDelete, value, size, itemClassName, onClick, selectable, tagData, isV2, ...rest },
    ref
  ) => {
    const deleteBtn = (
      <span className={styles.deleteButton} onClick={() => onDelete(value)}>
        {isV2 || rest?.designV2 ? <CloseIconV2 /> : <CloseCircleIcon />}
      </span>
    );

    return (
      <div
        ref={ref}
        className={cx(
          itemClassName || styles.selectedFilter,
          size === 'large' && styles.sizeLarge,
          tagData?.selected ? styles.selected : ''
        )}
        {...rest}>
        <span onClick={() => onClick?.(value)}>{label}</span>

        {selectable ? tagData?.selected && deleteBtn : deleteBtn}
      </div>
    );
  }
);
